import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import { Shape as PinShape } from 'next/entities/experiences';
import {
  SearchInput,
  GroupedFiltersView,
  Filter,
  SortDropdown,
} from 'next/components/ui';
import { Shape as TagsShape } from 'next/entities/tags';
import {
  Control,
  ControlSeparator,
  Controls,
  List,
  NoResults,
  ViewToggle,
} from 'next/components/Listing';
import { useControls } from './use-controls';
import Empty from './Empty';
import PinCard from './PinCard';

const createTagsOptions = (tags, currentOptions = {}) =>
  tags.reduce((acc, tag) => {
    acc[tag.id] = {
      label: tag.name,
      enabled: currentOptions[tag.id] ? currentOptions[tag.id].enabled : false,
      filterFn: experience => experience.tagIds.includes(tag.id),
    };
    return acc;
  }, {});

const sortTags = tags =>
  tags.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, {
      numeric: true,
    })
  );

export default function PinsList({ tags, pins }) {
  const {
    processed,
    search,
    sort,
    view,
    filters,
    updateFilters,
    handleFilterChange,
    handleFilterClear,
  } = useControls(
    { data: pins },
    {
      flow_status: {
        name: 'Flow Status',
        options: {
          draft: {
            label: 'Draft',
            enabled: false,
            filterFn: item => !item.published && item.state === 'DRAFT',
          },
          live: {
            label: 'Live',
            enabled: false,
            filterFn: item => item.published && item.state === 'DRAFT',
          },
          archived: {
            label: 'Include archived',
            enabled: false,
            separated: true,
            defaultHidden: true,
            filterFn: item => item.state === 'ARCHIVED',
          },
        },
      },
      tags: {
        name: 'Tag',
        options: createTagsOptions(sortTags(Object.values(tags))),
      },
    }
  );

  useEffect(() => {
    updateFilters(currentFilters => {
      const newTags = Object.values(tags);
      return {
        ...currentFilters,
        tags: {
          ...currentFilters.tags,
          options: {
            ...createTagsOptions(
              sortTags(newTags),
              currentFilters.tags.options
            ),
          },
        },
      };
    });
  }, [tags, updateFilters]);

  if (isEmpty(pins)) {
    return <Empty />;
  }

  return (
    <>
      <Controls>
        <Control>
          <SearchInput
            onChange={search.onChange}
            placeholder="Search by Pin name"
            value={search.value}
          />
        </Control>

        <Control>
          <Filter
            filters={filters}
            onChange={handleFilterChange}
            onClear={handleFilterClear}
            view={GroupedFiltersView}
          />
        </Control>

        <ControlSeparator />

        <Control>
          <SortDropdown
            onChange={sort.onChange}
            options={sort.options}
            value={sort.value}
          />
        </Control>

        <Control>
          <ViewToggle onChange={view.onChange} value={view.value} />
        </Control>
      </Controls>

      {isEmpty(processed) && (
        <NoResults>No results found. Please try different filters.</NoResults>
      )}

      {!isEmpty(processed) && (
        <List $kind={view.value}>
          {processed.map(pin => (
            <PinCard key={pin.id} pin={pin} view={view.value} />
          ))}
        </List>
      )}
    </>
  );
}

PinsList.propTypes = {
  tags: PropTypes.objectOf(TagsShape),
  pins: PropTypes.objectOf(PinShape),
};
